import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import type { Language } from "./translations";
import { LANGUAGES, LANGUAGE_LABELS } from "./translations";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  cycleLang: () => void;
  t: (obj: Record<Language, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const stored = sessionStorage.getItem("lang") as Language;
      return stored && LANGUAGES.includes(stored) ? stored : "en";
    }
    return "en";
  });

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
    sessionStorage.setItem("lang", newLang);
  }, []);

  const cycleLang = useCallback(() => {
    const idx = LANGUAGES.indexOf(lang);
    const next = LANGUAGES[(idx + 1) % LANGUAGES.length];
    setLang(next);
  }, [lang, setLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (obj: Record<Language, string>) => obj[lang],
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, cycleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
